import { Image, Text, View } from "@react-pdf/renderer";
import { Trans, useTranslation } from "react-i18next";
import { COMPRESSION_PILE, TENSION_PILE } from "../../../constants";
import Inch from "../../Math/Units/Imperial/Inch";
import Kilopound from "../../Math/Units/Imperial/Kilopound";
import KilopoundPerSquareInch from "../../Math/Units/Imperial/KilopoundPerSquareInch";
import KiloNewton from "../../Math/Units/Metric/KiloNewton";
import Megapascal from "../../Math/Units/Metric/Megapascal";
import Millimetre from "../../Math/Units/Metric/Millimetre";
import { DataRow, DataRowTextValue } from "../DataRow";
import { PageWrapper, styles, tableStyles } from "../SmartTitanProjektPDF";

export const ABC_URI = "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}";

export function imageURI(texformula) {
  return {
    uri: ABC_URI + texformula,
    method: "GET",
    headers: {},
  };
}

export function AASHTO_Innere({ data }) {
  const { t } = useTranslation("translation");
  let itemIndex = 1;
  let isMetric = data.Aashto.aashtoAll.measurementType == 0;
  let isImperial = data.Aashto.aashtoAll.measurementType == 1;
  let isCompression = data.Aashto.aashtoAll.loadType === COMPRESSION_PILE;
  let isTension = data.Aashto.aashtoAll.loadType === TENSION_PILE;
  return (
    <>
      <PageWrapper Deckblatt={data.Deckblatt}>
        <View>
          {/* Innere Tragfähigkeit */}
          <Text style={styles.section}>{t("inner.headline")}</Text>

          <View style={tableStyles.tableContainer}>
            {/* Imperial / Metric: */}
            <DataRowTextValue
              item={{
                title: t("aashto.Imperial / Metric"),
                value: t(isMetric ? "aashto.Metric" : "aashto.Imperial"),
                itemIndex: itemIndex++,
              }}
            />
            {/* Belastungsart/-Richtung */}
            <DataRowTextValue
              item={{
                title: t("inner.loadType"),
                value: t(isCompression ? "inner.compressionPile" : "inner.tensionPile"),
                itemIndex: itemIndex++,
              }}
            />
          </View>

          {/* Information Druck */}
          {isCompression ? (
            <>
              <Text style={styles.sectionSubText}>{t("aashto.inner.Compression")}</Text>

              <View style={{ marginRight: 60 }}>
                {/*
                RCU = ΦCURn for which Rn = 0.85 [0.85 f'c Ag + fyAb]
                ΦCU
                Resistance factor spcified in Table 10.5.5.2.5-2 for structural resitance of the uncased section of micropile in subjected to compression loading
                f'c
                Specified compressive strength of micropile grout at 28 days unless another and is specified (ksi)
                Ag
                Cross-Sectional Area of grout withing micropile (in2)
                Fy
                Specified minimum yield strength of reinforcement bar or stress in steel reinforcement bar at a strain of 0.003, whichever is less (ksi)
                Ab 
                Cross-sectional area of steel reinfocing bar (in2)
            */}
                <View style={{ flexDirection: "row" }}>
                  <Image
                    src={imageURI("R_{CU}=\\Phi_{CU}\\cdot&space;R_n")}
                    style={{
                      height: 7,
                      width: 61,
                      marginLeft: 10,
                    }}
                  />
                  <Text style={{ marginLeft: 10, fontSize: 8 }}>{t("aashto.inner.for which")}</Text>
                  <Image
                    src={imageURI(
                      "R_n=0.85\\cdot\\left[0.85\\cdot&space;f'_c\\cdot&space;A_g&plus;f_y\\cdot&space;A_b\\right]"
                    )}
                    style={{
                      height: 9,
                      width: 124,
                      marginLeft: 10,
                    }}
                  />
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("\\Phi_{CU}")}
                      style={{
                        height: 7,
                        width: 16,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    {t("aashto.inner.PhiCU_Description")}
                  </Text>
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("f'_c")}
                      style={{
                        height: 8,
                        width: 7,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.fc_Description"
                      values={{
                        unit: isMetric ? "MPa" : "ksi",
                      }}
                    />
                  </Text>
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("A_g")}
                      style={{
                        height: 9,
                        width: 10,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.Ag_Description"
                      values={{
                        unit: isMetric ? "mm²" : "in²",
                      }}
                    />
                  </Text>
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("F_y")}
                      style={{
                        height: 9,
                        width: 9,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.Fy_Description"
                      values={{
                        unit: isMetric ? "MPa" : "ksi",
                      }}
                    />
                  </Text>
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("A_b")}
                      style={{
                        height: 7,
                        width: 9,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.Ab_Description"
                      values={{
                        unit: isMetric ? "mm²" : "in²",
                      }}
                    />
                  </Text>
                </View>
              </View>
            </>
          ) : null}
          {/* Information Zug */}
          {isTension ? (
            <>
              <Text style={styles.sectionSubText}>{t("aashto.inner.Tension")}</Text>

              <View style={{ marginRight: 60 }}>
                {/* RTU = ΦTURn for which Rn = FyAb
                ΦTU
                Resistance factor spcified in Table 10.5.5.2.5-2 for structural resitance of the uncased section of micropile in subjected to tension loading
                Fy
                Specified minimum yield strength of reinforcement bar or stress in steel reinforcement bar at a strain of 0.003, whichever is less (ksi)
                Ab
                Cross-sectional area of steel reinfocing bar (in2)
            */}
                <View style={{ flexDirection: "row" }}>
                  {/* R_s=\pi d_{b}\alpha_{d} L_{b} 
                  https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{TU}=\\Phi_{TU}\\cdot&space;R_n

              */}
                  <Image
                    src={imageURI("R_{TU}=\\Phi_{TU}\\cdot&space;R_n")}
                    style={{
                      height: 7,
                      width: 60,
                      marginLeft: 10,
                    }}
                  />
                  <Text style={{ marginLeft: 10, fontSize: 8 }}>{t("aashto.inner.for which")}</Text>
                  <Image
                    src={imageURI("R_n=F_y\\cdot&space;A_b")}
                    style={{
                      height: 9,
                      width: 46,
                      marginLeft: 10,
                    }}
                  />
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("\\Phi_{TU}")}
                      style={{
                        height: 7,
                        width: 16,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    {t("aashto.inner.PhiTU_Description")}
                  </Text>
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("F_y")}
                      style={{
                        height: 9,
                        width: 9,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.Fy_Description"
                      values={{
                        unit: isMetric ? "MPa" : "ksi",
                      }}
                    />
                  </Text>
                </View>

                <View style={{ flexDirection: "row" }}>
                  <View style={{ width: 20 }}>
                    <Image
                      src={imageURI("A_b")}
                      style={{
                        height: 7,
                        width: 9,
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                    />
                  </View>
                  <Text style={{ marginTop: 5, marginLeft: 10, fontSize: 8 }}>
                    <Trans
                      i18nKey="aashto.inner.Ab_Description"
                      values={{
                        unit: isMetric ? "mm²" : "in²",
                      }}
                    />
                  </Text>
                </View>
              </View>
            </>
          ) : null}

          <Text style={styles.sectionSubText}>{t("aashto.inner.Pd Calculation_pdf")}</Text>

          <View style={tableStyles.tableContainer}>
            {/* Bemessungslast */}
            <DataRow
              item={{
                title: t("aashto.inner.Total Design Load"),
                bezImage: {
                  uri: ABC_URI + "P_{d}",
                  height: 7,
                  width: 9,
                },
                value: data.Aashto.aashtoAll.totalDesignLoad.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
              }}
            />
          </View>

          {/*
            
            Fd Calculation:
            Design Combination  Strength 1, Max
            Dead Load               20      [kip]x 1.25
            Live Load               30      [kip]x 1.75
            Total Design Load   Fd  77.5    [kip]
            */}

          <Text style={styles.sectionSubText}>
            {t("aashto.inner.Fd Verification of the load-bearing capacity")}
          </Text>

          <View style={tableStyles.tableContainer}>
            {/* Selected Load Carrying Element: */}
            <DataRowTextValue
              item={{
                title: t("aashto.inner.Selected Load Carrying Element"),
                value: data.Aashto.aashtoAll.selectedCarryingElement.name,
                itemIndex: itemIndex++,
              }}
            />

            {/* Bar OD */}
            <DataRow
              item={{
                title: t("aashto.inner.Bar OD"),
                value: data.Aashto.aashtoAll.barOD.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Millimetre /> : <Inch />,
              }}
            />

            {/* Area of Steel */}
            <DataRow
              item={{
                title: t("aashto.inner.Area of Steel"),
                bezImage: {
                  uri: ABC_URI + "A_{b}",
                  height: 7,
                  width: 9,
                },
                value: data.Aashto.aashtoAll.areaOfSteel.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? "mm²" : "in²",
              }}
            />

            {/* Fy */}
            <DataRow
              item={{
                title: "Fy",
                value: data.Aashto.aashtoAll.fy.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Megapascal /> : <KilopoundPerSquareInch />,
              }}
            />

            {/* Py */}
            <DataRow
              item={{
                title: "Py",
                value: data.Aashto.aashtoAll.py.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
              }}
            />

            {/* Fy (Controlling) */}
            <DataRow
              item={{
                title: t("aashto.inner.Fy(Controlling)"),
                value: data.Aashto.aashtoAll.fyControlling.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Megapascal /> : <KilopoundPerSquareInch />,
              }}
            />

            {/*  28 day grout strength */}
            {isCompression ? (
              <DataRow
                item={{
                  title: t("aashto.inner.Choose 28-day grout strength"),
                  bezImage: {
                    uri: ABC_URI + "f'_{c}",
                    height: 7,
                    width: 9,
                  },
                  value: data.Aashto.aashtoAll.twentyEightDayGroutStrength.current,
                  valueScale: 2,
                  itemIndex: itemIndex++,
                  valueUnit: isMetric ? <Megapascal /> : <KilopoundPerSquareInch />,
                }}
              />
            ) : null}

            {/* Bit/Hole Size */}
            <DataRow
              item={{
                title: t("aashto.inner.Bit/Hole Size"),
                value: data.Aashto.aashtoAll.holeSize.current,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: isMetric ? <Millimetre /> : <Inch />,
              }}
            />

            {/* Area of Grout */}
            {isCompression ? (
              <DataRow
                item={{
                  title: t("aashto.inner.Area of Grout"),
                  bezImage: {
                    uri: ABC_URI + "A_{g}",
                    height: 7,
                    width: 9,
                  },
                  formulaText: t("aashto.inner.Ag_InfoText"),
                  value: data.Aashto.aashtoAll.ag.current,
                  valueScale: 2,
                  itemIndex: itemIndex++,
                  valueUnit: isMetric ? "mm²" : "in²",
                }}
              />
            ) : null}

            {/* Resistance Factor compression pile*/}
            {isCompression ? (
              <DataRow
                item={{
                  title: t("aashto.inner.Resistance Factor"),
                  bezImage: {
                    uri: ABC_URI + "\\Phi_{CU}",
                    height: 7,
                    width: 16,
                  },
                  formulaText: t("aashto.inner.ResistanceFactorInfoText"),
                  value: data.Aashto.aashtoAll.resistanceFactor,
                  valueScale: 2,
                  itemIndex: itemIndex++,
                }}
              />
            ) : null}

            {/* Resistance Factor tension pile*/}
            {isTension ? (
              <DataRow
                item={{
                  title: t("aashto.inner.Resistance Factor"),
                  bezImage: {
                    uri: ABC_URI + "\\Phi_{TU}",
                    height: 7,
                    width: 17,
                  },
                  formulaText: t("aashto.inner.ResistanceFactorInfoText"),
                  value: data.Aashto.aashtoAll.resistanceFactor,
                  valueScale: 2,
                  itemIndex: itemIndex++,
                }}
              />
            ) : null}

            {/* Factored Compression Resistance */}
            {isCompression ? (
              <DataRow
                item={{
                  bezImage: {
                    uri: ABC_URI + "R_{n}",
                    height: 7,
                    width: 10,
                  },
                  formulaImage: {
                    uri:
                      ABC_URI +
                      "R_n=0.85\\cdot\\left[0.85\\cdot&space;f'_c\\cdot&space;A_g&plus;f_y\\cdot&space;A_b\\right]",
                    height: 9,
                    width: 124,
                  },
                  value: data.Aashto.aashtoAll.factoredCompressionResistance.current,
                  valueScale: 2,
                  itemIndex: itemIndex++,
                  valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
                }}
              />
            ) : null}

            {/* RCU Compression */}
            {isCompression ? (
              <DataRow
                item={{
                  title: t("aashto.inner.Factored Compression Resistance"),
                  bezImage: {
                    uri: ABC_URI + "R_{CU}",
                    height: 7,
                    width: 16,
                  },
                  formulaImage: {
                    uri:
                      ABC_URI +
                      "R_{\\small&space;_{CU}}=\\Phi_{\\small&space;_{CU}}\\cdot&space;R_n",
                    height: 8,
                    width: 64,
                  },
                  value: data.Aashto.aashtoAll.rcu,
                  valueScale: 2,
                  itemIndex: itemIndex++,
                }}
              />
            ) : null}

            {/* RTU Tension */}
            {isTension ? (
              <DataRow
                item={{
                  title: t("aashto.inner.Factored Tension Resistance"),
                  bezImage: {
                    uri: ABC_URI + "R_{TU}",
                    height: 7,
                    width: 16,
                  },
                  formulaImage: {
                    uri:
                      ABC_URI +
                      "R_{\\small&space;_{TU}}=\\Phi_{\\small&space;_{TU}}\\cdot&space;(F_y\\cdot&space;A_b)",
                    height: 9,
                    width: 78,
                  },
                  value: data.Aashto.aashtoAll.factoredTensionResistance.current,
                  valueScale: 2,
                  itemIndex: itemIndex++,
                  valueUnit: isMetric ? <KiloNewton /> : <Kilopound />,
                }}
              />
            ) : null}

            {/* Utilization */}
            <DataRow
              item={{
                title: t("aashto.inner.Utilization"),
                value: data.Aashto.aashtoAll.utilization,
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
          </View>
        </View>
      </PageWrapper>
    </>
  );
}
